import { useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Checkbox } from "../components/ui/checkbox";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import { ScrollArea } from "../components/ui/scroll-area";
import { FileText, Shield, CheckCircle2, Loader2, ExternalLink } from "lucide-react";

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AcceptCGUPage({ onAccepted }) {
  const navigate = useNavigate();
  const [cguAccepted, setCguAccepted] = useState(false);
  const [privacyAccepted, setPrivacyAccepted] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async () => {
    if (!cguAccepted) {
      toast.error("Veuillez accepter les Conditions Générales d'Utilisation");
      return;
    }
    if (!privacyAccepted) {
      toast.error("Veuillez accepter la Politique de Confidentialité");
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API_URL}/auth/accept-cgu`, {
        cgu_accepted: true,
        privacy_accepted: true
      });
      
      toast.success("Conditions acceptées avec succès");
      
      if (onAccepted) {
        onAccepted();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur lors de la validation");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center p-4">
      <Card className="w-full max-w-3xl shadow-xl" data-testid="accept-cgu-card">
        <CardHeader className="bg-primary text-white rounded-t-lg">
          <div className="flex items-center gap-3">
            <Shield className="w-8 h-8" />
            <div>
              <CardTitle className="text-2xl font-heading">Conditions d'utilisation</CardTitle>
              <CardDescription className="text-primary-foreground/80">
                Inspection Générale du Travail - Plateforme Bilan Social
              </CardDescription>
            </div>
          </div>
        </CardHeader>
        
        <CardContent className="p-6 space-y-6">
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
            <p className="text-amber-800 text-sm">
              <strong>Important :</strong> Avant d'accéder à la plateforme, vous devez lire et accepter 
              les Conditions Générales d'Utilisation ainsi que la Politique de Confidentialité.
            </p>
          </div>

          {/* CGU Section */}
          <div className="space-y-3">
            <div className="flex items-center gap-2 text-primary font-semibold">
              <FileText className="w-5 h-5" />
              <h3>Conditions Générales d'Utilisation (CGU)</h3>
            </div>
            <ScrollArea className="h-48 border rounded-lg p-4 bg-slate-50">
              <div className="text-sm text-slate-700 space-y-3 pr-4">
                <p><strong>Article 1 - Objet</strong></p>
                <p>
                  Les présentes Conditions Générales d'Utilisation (CGU) régissent l'accès et l'utilisation 
                  de la plateforme de déclaration du bilan social de l'Inspection Générale du Travail (IGT) 
                  de la République de Guinée.
                </p>
                
                <p><strong>Article 2 - Accès à la plateforme</strong></p>
                <p>
                  L'accès à la plateforme est réservé aux cabinets habilités par l'IGT. Chaque cabinet 
                  s'engage à utiliser ses identifiants de manière confidentielle et à ne pas les partager 
                  avec des tiers.
                </p>
                
                <p><strong>Article 3 - Obligations du cabinet habilité</strong></p>
                <p>Le cabinet habilité s'engage à :</p>
                <ul className="list-disc pl-5 space-y-1">
                  <li>Fournir des informations exactes et vérifiables</li>
                  <li>Respecter les délais de déclaration fixés par la réglementation</li>
                  <li>Conserver la confidentialité des données des entreprises clientes</li>
                  <li>Signaler toute anomalie ou tentative de fraude</li>
                  <li>Ne pas utiliser la plateforme à des fins illicites</li>
                </ul>
                
                <p><strong>Article 4 - Responsabilité</strong></p>
                <p>
                  Le cabinet habilité est responsable de l'exactitude des déclarations soumises. 
                  Toute fausse déclaration peut entraîner la suspension ou la révocation de l'habilitation, 
                  sans préjudice des poursuites judiciaires.
                </p>
                
                <p><strong>Article 5 - Propriété intellectuelle</strong></p>
                <p>
                  La plateforme et son contenu sont la propriété exclusive de l'IGT. Toute reproduction 
                  ou utilisation non autorisée est interdite.
                </p>
                
                <p><strong>Article 6 - Modification des CGU</strong></p>
                <p>
                  L'IGT se réserve le droit de modifier les présentes CGU. Les utilisateurs seront 
                  informés de toute modification substantielle.
                </p>
                
                <p><strong>Article 7 - Droit applicable</strong></p>
                <p>
                  Les présentes CGU sont régies par le droit guinéen. Tout litige sera soumis aux 
                  juridictions compétentes de Conakry.
                </p>
              </div>
            </ScrollArea>
            
            <div className="flex items-start space-x-3 pt-2">
              <Checkbox
                id="cgu"
                checked={cguAccepted}
                onCheckedChange={setCguAccepted}
                data-testid="cgu-checkbox"
              />
              <Label 
                htmlFor="cgu" 
                className="text-sm font-medium leading-tight cursor-pointer"
              >
                J'ai lu et j'accepte les Conditions Générales d'Utilisation de la plateforme IGT Bilan Social
                <span className="text-red-500 ml-1">*</span>
              </Label>
            </div>
          </div>

          {/* Privacy Policy Section */}
          <div className="space-y-3">
            <div className="flex items-center gap-2 text-primary font-semibold">
              <Shield className="w-5 h-5" />
              <h3>Politique de Confidentialité</h3>
            </div>
            <ScrollArea className="h-48 border rounded-lg p-4 bg-slate-50">
              <div className="text-sm text-slate-700 space-y-3 pr-4">
                <p><strong>1. Collecte des données</strong></p>
                <p>
                  Dans le cadre de l'utilisation de la plateforme, l'IGT collecte les données suivantes :
                </p>
                <ul className="list-disc pl-5 space-y-1">
                  <li>Informations d'identification du cabinet (nom, RCCM, NIF, adresse)</li>
                  <li>Coordonnées du responsable (nom, prénom, email, téléphone)</li>
                  <li>Données relatives aux entreprises clientes et leurs bilans sociaux</li>
                  <li>Données de connexion et d'utilisation de la plateforme</li>
                </ul>
                
                <p><strong>2. Finalités du traitement</strong></p>
                <p>Les données collectées sont utilisées pour :</p>
                <ul className="list-disc pl-5 space-y-1">
                  <li>Gérer les habilitations des cabinets</li>
                  <li>Traiter et valider les déclarations de bilan social</li>
                  <li>Produire des statistiques anonymisées sur l'emploi en Guinée</li>
                  <li>Communiquer avec les cabinets habilités</li>
                  <li>Assurer la sécurité de la plateforme</li>
                </ul>
                
                <p><strong>3. Destinataires des données</strong></p>
                <p>
                  Les données sont accessibles uniquement aux agents habilités de l'IGT et du Ministère 
                  du Travail et de la Fonction Publique. Aucune donnée n'est transmise à des tiers sans 
                  consentement, sauf obligation légale.
                </p>
                
                <p><strong>4. Durée de conservation</strong></p>
                <p>
                  Les données sont conservées pendant la durée de l'habilitation du cabinet, puis 
                  archivées conformément aux obligations légales de conservation des documents administratifs.
                </p>
                
                <p><strong>5. Sécurité des données</strong></p>
                <p>
                  L'IGT met en œuvre des mesures techniques et organisationnelles pour protéger les 
                  données contre tout accès non autorisé, modification ou destruction.
                </p>
                
                <p><strong>6. Droits des utilisateurs</strong></p>
                <p>
                  Conformément à la réglementation applicable, vous disposez d'un droit d'accès, de 
                  rectification et de suppression de vos données. Pour exercer ces droits, contactez 
                  l'IGT à l'adresse : info@igt.gov.gn
                </p>
                
                <p><strong>7. Cookies</strong></p>
                <p>
                  La plateforme utilise des cookies techniques nécessaires à son fonctionnement. 
                  Ces cookies ne collectent pas de données personnelles à des fins publicitaires.
                </p>
              </div>
            </ScrollArea>
            
            <div className="flex items-start space-x-3 pt-2">
              <Checkbox
                id="privacy"
                checked={privacyAccepted}
                onCheckedChange={setPrivacyAccepted}
                data-testid="privacy-checkbox"
              />
              <Label 
                htmlFor="privacy" 
                className="text-sm font-medium leading-tight cursor-pointer"
              >
                J'ai lu et j'accepte la Politique de Confidentialité et le traitement de mes données personnelles
                <span className="text-red-500 ml-1">*</span>
              </Label>
            </div>
          </div>

          {/* Submit Button */}
          <div className="pt-4 border-t">
            <Button
              onClick={handleSubmit}
              disabled={!cguAccepted || !privacyAccepted || loading}
              className="w-full h-12 text-lg"
              data-testid="accept-cgu-button"
            >
              {loading ? (
                <>
                  <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                  Validation en cours...
                </>
              ) : (
                <>
                  <CheckCircle2 className="w-5 h-5 mr-2" />
                  Accepter et continuer
                </>
              )}
            </Button>
            
            {(!cguAccepted || !privacyAccepted) && (
              <p className="text-center text-sm text-muted-foreground mt-3">
                Vous devez cocher les deux cases pour continuer
              </p>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
